import React, { useState, useEffect } from 'react';
import { TrendingUp, Award, Target, ChevronRight, Loader2, Flame, Zap } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useNavigate } from 'react-router-dom';

const SocialScoreCard = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScore();
  }, []);

  const fetchScore = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getCurrentScore();
      setScore(data);
    } catch (err) {
      console.error('Failed to fetch social score:', err);
      setScore({
        overall_score: 45,
        empathy: 50,
        clarity: 40,
        assertiveness: 45,
        level: 1,
        xp: 0,
        xp_to_next_level: 200,
        real_world: { xp: 0, total_actions: 0, week_actions: 0, streak: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (value) => {
    if (value >= 80) return 'text-green-600';
    if (value >= 60) return 'text-blue-600';
    if (value >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressColor = (value) => {
    if (value >= 80) return 'from-green-400 to-green-600';
    if (value >= 60) return 'from-blue-400 to-blue-600';
    if (value >= 40) return 'from-yellow-400 to-yellow-600';
    return 'from-red-400 to-red-600';
  };

  if (loading) {
    return (
      <div className="card" data-testid="social-score-card">
        <div className="card-header">
          <h2 className="card-title">Social Score</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (!score) return null;

  const xp = score.xp || 0;
  const xpToNext = score.xp_to_next_level || 200;
  const xpProgress = (xp % 200) / 200 * 100;
  const rw = score.real_world || {};

  return (
    <div className="card" data-testid="social-score-card">
      <div className="card-header">
        <h2 className="card-title">Social Score</h2>
        <button
          onClick={() => navigate('/challenges')}
          className="card-action"
          data-testid="social-score-details"
        >
          Details
        </button>
      </div>

      {/* Overall Score Circle */}
      <div className="flex items-center justify-center mb-4">
        <div className="relative w-24 h-24">
          <svg className="w-24 h-24 transform -rotate-90">
            <circle cx="48" cy="48" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
            <circle
              cx="48" cy="48" r="40"
              stroke="url(#scoreGradient)"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${(score.overall_score / 100) * 251.2} 251.2`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3B82F6" />
                <stop offset="100%" stopColor="#8B5CF6" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-2xl font-bold ${getScoreColor(score.overall_score)}`}>
              {score.overall_score}
            </span>
            <span className="text-xs text-gray-600">Overall</span>
          </div>
        </div>
      </div>

      {/* Level & XP */}
      <div className="flex items-center justify-between mb-2 p-2 bg-purple-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Award size={18} className="text-purple-600" />
          <span className="font-semibold text-purple-900" data-testid="social-score-level">Level {score.level}</span>
        </div>
        <span className="text-xs text-purple-700" data-testid="social-score-xp">{xp} XP</span>
      </div>

      {/* XP Progress Bar */}
      <div className="w-full bg-purple-200 rounded-full h-2 mb-4 overflow-hidden">
        <div
          className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 rounded-full transition-all duration-500"
          style={{ width: `${xpProgress}%` }}
        />
      </div>

      {/* Real-World Impact Banner */}
      {rw.total_actions > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl" data-testid="real-world-impact">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={14} className="text-green-600" />
            <span className="text-xs font-bold text-green-800 uppercase tracking-wider">Real-World Impact</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-green-700">{rw.total_actions}</div>
              <div className="text-[10px] text-green-600">Actions</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-700 flex items-center justify-center gap-1">
                {rw.streak}<Flame size={12} className="text-orange-500" />
              </div>
              <div className="text-[10px] text-green-600">Streak</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-700">+{rw.xp}</div>
              <div className="text-[10px] text-green-600">XP</div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Scores */}
      <div className="space-y-3">
        {[
          { label: 'Empathy', value: score.empathy },
          { label: 'Clarity', value: score.clarity },
          { label: 'Assertiveness', value: score.assertiveness }
        ].map((item) => (
          <div key={item.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700">{item.label}</span>
              <span className={`text-sm font-semibold ${getScoreColor(item.value)}`}>
                {item.value}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div
                className={`bg-gradient-to-r ${getProgressColor(item.value)} h-1.5 rounded-full transition-all duration-500`}
                style={{ width: `${item.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA to log real-world actions */}
      <button
        onClick={() => navigate('/challenges')}
        className="w-full mt-4 py-2 px-3 rounded-lg border border-green-200 hover:border-green-300 hover:bg-green-50 transition-all flex items-center justify-between"
        data-testid="log-real-world-action-btn"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Target size={16} className="text-green-500" />
          Log Real-World Action
        </span>
        <ChevronRight size={16} className="text-gray-400" />
      </button>
    </div>
  );
};

export default SocialScoreCard;
